import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteDefectRequest,
    deleteDefectReset,
    editDefectRequest,
    editDefectReset,
    getDefectRequest,
    getDefectReset,
} from '../store';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import {
    selectDefect,
    selectDeleteDefectError,
    selectDeleteDefectStatus,
    selectEditDefectError,
    selectEditDefectStatus,
    selectGetDefectError,
    selectGetDefectStatus,
} from '../store/selectors';
import { useTranslation } from 'react-i18next';
import { AutoLinkPattern, AutoLinkPatternDialogAction, DefectStatusEnum } from '../types';
import { RequestStatusEnum } from '@shared/types';
import DefectFormDialog from './DefectFormDialog';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';
import ModalDelete from '@shared/components/modals/ModalDelete';
import { UI_DEFECTS_PATH } from '@shared/constants/pathsConstants';
import TestrunsTable from '@pages/testruns/components/TestrunsTable';
import { TestcasesTable } from '@pages/testcases';
import TestrunResultsTable from '@pages/testruns/components/TestrunResultsTable';
import DefectAutoLinkPatternDialog from './DefectAutoLinkPatternDialog';
import LoadingPlaceholder from '@shared/components/ui/LoadingPlaceHolder';
import DefectCardHeader from './DefectCardHeader';
import DefectCardInfo from './DefectCardInfo';
import DefectAutoLinkPatternsList from './DefectAutoLinkPatternsList';
import { uuidv4 } from '@shared/utils/utilsOld';

const DEFAULT_PATTERN: AutoLinkPattern = {
    pk: uuidv4(),
    name: '',
    errorMessagePattern: '',
    stackTracePattern: '',
};

const DefectCard: FC = () => {
    const dispatch = useDispatch();
    const { defectId } = useParams();
    const defect = useSelector(selectDefect);
    const getDefectStatus = useSelector(selectGetDefectStatus);
    const editDefectStatus = useSelector(selectEditDefectStatus);
    const deleteDefectStatus = useSelector(selectDeleteDefectStatus);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isDefectLoading = getDefectStatus === RequestStatusEnum.Loading;
    const isDefectLoaded = getDefectStatus === RequestStatusEnum.Success;
    const [openDeleteDefectModal, setOpenDeleteDefectModal] = useState(false);
    const [openDefectEditDialog, setOpenDefectEditDialog] = useState(false);
    const [autoLinkPatternDialog, setAutoLinkPatternDialog] = useState<{
        open: boolean;
        action: AutoLinkPatternDialogAction;
        patternIndex?: number;
    }>({ open: false, action: 'add' });
    const [deleteAutoLinkPatternIndex, setDeleteAutoLinkPatternIndex] = useState<number | null>(null);

    useEffect(() => {
        if (defectId) {
            dispatch(getDefectRequest({ defectId: Number(defectId) }));
        }
    }, [defectId, dispatch]);

    const handleCloseDefect = () => {
        dispatch(editDefectRequest({ pk: Number(defectId), status: DefectStatusEnum.CLOSED }));
    };

    const handleReopenDefect = () => {
        dispatch(editDefectRequest({ pk: Number(defectId), status: DefectStatusEnum.OPEN }));
    };

    const handleDeleteDefect = () => {
        dispatch(deleteDefectRequest({ defectId: Number(defectId) }));
        navigate(`/${UI_DEFECTS_PATH}`);
    };

    const handleSubmitAutoLinkPattern = (data: AutoLinkPattern) => {
        if (!defect) return;

        const { action, patternIndex } = autoLinkPatternDialog;

        if (action === 'add') {
            dispatch(
                editDefectRequest({
                    pk: Number(defectId),
                    autoLinkPatterns: [...defect.autoLinkPatterns, data],
                }),
            );
        } else if (action === 'edit' && patternIndex !== undefined) {
            const updated = defect.autoLinkPatterns.map((p, i) => (i === patternIndex ? data : p));
            dispatch(
                editDefectRequest({
                    pk: Number(defectId),
                    autoLinkPatterns: updated,
                }),
            );
        }
    };

    const handleDeleteAutoLinkPattern = () => {
        if (defect && deleteAutoLinkPatternIndex !== null) {
            dispatch(
                editDefectRequest({
                    pk: Number(defectId),
                    autoLinkPatterns: defect.autoLinkPatterns.filter(
                        (_, index) => index !== deleteAutoLinkPatternIndex,
                    ),
                }),
            );
            setDeleteAutoLinkPatternIndex(null);
        }
    };

    const getAutoLinkPatternDefaultValues = () => {
        const { action, patternIndex } = autoLinkPatternDialog;

        if (action === 'edit' && patternIndex !== undefined && defect?.autoLinkPatterns[patternIndex]) {
            return { pattern: defect.autoLinkPatterns[patternIndex] };
        }

        return { pattern: DEFAULT_PATTERN };
    };

    if (!defectId && !isDefectLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Typography sx={{ fontSize: 20, color: 'text.secondary' }}>{t('no_defect_selected')}...</Typography>
                {deleteDefectStatus === RequestStatusEnum.Success && (
                    <ModalSuccessContainer
                        action={t('deleted')}
                        object={t('defect')}
                        resetActionCallback={deleteDefectReset}
                    />
                )}
                {deleteDefectStatus === RequestStatusEnum.Error && (
                    <ModalErrorContainer
                        object={t('defect_r').toLowerCase()}
                        action={t('deleting')}
                        errorSelector={selectDeleteDefectError}
                        resetActionCallback={deleteDefectReset}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <DefectCardHeader
                        defect={defect}
                        onEdit={() => setOpenDefectEditDialog(true)}
                        onClose={handleCloseDefect}
                        onReopen={handleReopenDefect}
                        onDelete={() => setOpenDeleteDefectModal(true)}
                    />
                    <DefectCardInfo defect={defect} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <LoadingPlaceholder
                        isLoading={isDefectLoading}
                        skeleton={
                            <>
                                <Skeleton width={71} animation="wave" />
                                <Skeleton height={120} animation="wave" />
                            </>
                        }
                    >
                        {isDefectLoaded && defect && (
                            <>
                                <Typography sx={{ fontWeight: 700 }}>{t('description')}</Typography>
                                <Typography>{defect.description}</Typography>
                            </>
                        )}
                    </LoadingPlaceholder>
                </Box>
            </Box>
            <Divider flexItem sx={{ borderWidth: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <LoadingPlaceholder isLoading={isDefectLoading} skeleton={<Skeleton animation="wave" width={65} />}>
                    {isDefectLoaded && <Typography sx={{ fontWeight: 700 }}>{t('test_runs')}</Typography>}
                </LoadingPlaceholder>
                {defect?.testRunsCount ? (
                    <TestrunsTable customColumns={['pk', 'title', 'results', 'status']} defectId={defectId} />
                ) : (
                    <LoadingPlaceholder
                        isLoading={isDefectLoading}
                        skeleton={<Skeleton animation="wave" width={120} />}
                    >
                        {isDefectLoaded && (
                            <Typography sx={{ color: 'text.secondary' }}>
                                {t('no_objects_yet', { objects: t('test_runs_r') })}
                            </Typography>
                        )}
                    </LoadingPlaceholder>
                )}
            </Box>
            <Divider flexItem sx={{ borderWidth: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <LoadingPlaceholder isLoading={isDefectLoading} skeleton={<Skeleton animation="wave" width={70} />}>
                    {isDefectLoaded && <Typography sx={{ fontWeight: 700 }}>{t('test_cases')}</Typography>}
                </LoadingPlaceholder>
                {defect?.testCasesCount ? (
                    <TestcasesTable defectId={defectId} customColumns={['pk', 'name', 'module', 'status']} />
                ) : (
                    <LoadingPlaceholder
                        isLoading={isDefectLoading}
                        skeleton={<Skeleton animation="wave" width={130} />}
                    >
                        {isDefectLoaded && (
                            <Typography sx={{ color: 'text.secondary' }}>
                                {t('no_objects_yet', { objects: t('test_cases_r') })}
                            </Typography>
                        )}
                    </LoadingPlaceholder>
                )}
            </Box>
            <Divider flexItem sx={{ borderWidth: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <LoadingPlaceholder isLoading={isDefectLoading} skeleton={<Skeleton animation="wave" width={85} />}>
                    {isDefectLoaded && <Typography sx={{ fontWeight: 700 }}>{t('test_results')}</Typography>}
                </LoadingPlaceholder>
                {defect?.testResultsCount ? (
                    <TestrunResultsTable
                        defectId={defectId}
                        customColumns={['pk', 'testcaserun', 'result', 'status']}
                    />
                ) : (
                    <LoadingPlaceholder
                        isLoading={isDefectLoading}
                        skeleton={<Skeleton animation="wave" width={145} />}
                    >
                        {isDefectLoaded && (
                            <Typography sx={{ color: 'text.secondary' }}>
                                {t('no_objects_yet', { objects: t('test_results_r') })}
                            </Typography>
                        )}
                    </LoadingPlaceholder>
                )}
            </Box>
            <Divider flexItem sx={{ borderWidth: 2 }} />
            <DefectAutoLinkPatternsList
                patterns={defect?.autoLinkPatterns ?? []}
                isDefectLoaded={isDefectLoaded}
                isDefectLoading={isDefectLoading}
                onAdd={() => setAutoLinkPatternDialog({ open: true, action: 'add' })}
                onEdit={(index) => setAutoLinkPatternDialog({ open: true, action: 'edit', patternIndex: index })}
                onDelete={(index) => setDeleteAutoLinkPatternIndex(index)}
            />
            <DefectAutoLinkPatternDialog
                open={autoLinkPatternDialog.open}
                onClose={() => setAutoLinkPatternDialog({ open: false, action: 'add' })}
                onSubmit={handleSubmitAutoLinkPattern}
                action={autoLinkPatternDialog.action}
                defaultValues={getAutoLinkPatternDefaultValues()}
            />
            <DefectFormDialog
                open={openDefectEditDialog}
                onClose={() => setOpenDefectEditDialog(false)}
                mode="edit"
                defect={defect}
            />
            <ModalDelete
                open={deleteAutoLinkPatternIndex !== null}
                setOpen={(open: boolean) => {
                    if (!open) setDeleteAutoLinkPatternIndex(null);
                }}
                handleSubmit={handleDeleteAutoLinkPattern}
                objectName={t('automation_rule').toLowerCase()}
            />
            <ModalDelete
                open={openDeleteDefectModal}
                setOpen={setOpenDeleteDefectModal}
                handleSubmit={handleDeleteDefect}
                objectName={t('defect').toLowerCase()}
            />
            {getDefectStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defect_r').toLowerCase()}
                    action={t('fetching')}
                    errorSelector={selectGetDefectError}
                    resetActionCallback={getDefectReset}
                />
            )}
            {editDefectStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defect_r').toLowerCase()}
                    action={t('editing')}
                    errorSelector={selectEditDefectError}
                    resetActionCallback={editDefectReset}
                />
            )}
            {editDefectStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer
                    action={t('edited')}
                    object={t('defect')}
                    resetActionCallback={editDefectReset}
                />
            )}
        </Box>
    );
};

export default DefectCard;
